import { Button, TextField } from '@petsmart-ui/sparky';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClearRefinements, useRefinementList, useStats } from 'react-instantsearch';

import { configDynamicFacets, FILTERS, isEligibleValue } from '@libs/algolia';
import { trackApplyFacetedFilters, trackDrawerFilter, trackPLPFilters } from '@libs/analytics';

import { closeButtonDropdown } from '@/utils/closeButtonDropdown';
import { clearRefinementSearch } from '@/utils/refinementsListUtils';

import FilterRefinementListItem from './FilterRefinementListItem';
import type { FilterRefinementListProps } from './types';

import '@/components/FilterRefinementList/FilterRefinementList.scss';

export default function FilterRefinementList({
    attribute,
    pageId,
    isDropdownOpen = false,
    showActions = FILTERS.REFINEMENT_LIST.SHOW_ACTIONS,
    placeholder = FILTERS.REFINEMENT_LIST.SEARCH_PLACEHOLDER,
    searchable = FILTERS.REFINEMENT_LIST.SEARCHABLE,
    limit = FILTERS.REFINEMENT_LIST.LIMIT,
    showMoreLimit = FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    showMore = FILTERS.REFINEMENT_LIST.SHOW_MORE,
    isDrawer,
}: FilterRefinementListProps) {
    const transformItems = useCallback(
        (incomingItems: RefinementListItem[]) => {
            if (attribute === FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE) {
                return incomingItems
                    .filter(item => isEligibleValue(item.value))
                    .map(item => ({ ...item, label: FILTERS.SUBSCRIPTION_ENABLED.ELIGIBLE_LABEL }));
            }
            if (attribute === FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE) {
                return incomingItems
                    .filter(item => isEligibleValue(item.value))
                    .map(item => ({ ...item, label: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ELIGIBLE_LABEL }));
            }
            return incomingItems;
        },
        [attribute]
    );

    const { items, refine, searchForItems, canToggleShowMore, isShowingMore, toggleShowMore } = useRefinementList({
        attribute,
        limit,
        showMore,
        showMoreLimit,
        transformItems,
    });

    const { refine: clearRefine } = useClearRefinements({
        includedAttributes: [attribute],
    });

    const { nbHits } = useStats();
    const selectedOption = useRef<string>('');
    const [hasLog, setHasLog] = useState<boolean>(false);
    const [searchFieldValue, setSearchFieldValue] = useState<string>('');

    const handleClear = () => {
        clearRefine();
        clearRefinementSearch(searchForItems, setSearchFieldValue);
    };

    const hasFilterBrand = attribute === FILTERS.BRAND.ATTRIBUTE && !!items.find(item => item.isRefined);

    const listClasses = [
        'custom-filter-refinement-list__container',
        'sparky-u-margin-bottom-sm',
        showMore && 'custom-filter-refinement-list__container--has-show-more',
        searchable && 'custom-filter-refinement-list__container--has-search-box',
        hasFilterBrand && 'custom-filter-refinement-brand--selected',
        isDrawer && 'drawer-filter',
    ]
        .filter(Boolean)
        .join(' ');

    const debounceLog = debounce(() => {
        setHasLog(true);
    }, 1200);

    const handlerAnalytics = (value: string, isRefined: boolean) => {
        if (isRefined) {
            selectedOption.current = value;
            debounceLog();
        }
    };

    useEffect(() => {
        clearRefinementSearch(searchForItems, setSearchFieldValue);
    }, [isDropdownOpen]);

    useEffect(() => {
        if (selectedOption.current) {
            const title = configDynamicFacets[attribute]?.title;
            const value = selectedOption.current;
            trackDrawerFilter({
                filterType: title,
                filterSelection: value,
                results: nbHits,
            });
            trackPLPFilters(title, value);
            trackApplyFacetedFilters(title, value, pageId);
            selectedOption.current = '';
            setHasLog(false);
        }
    }, [hasLog]);

    return (
        <>
            <div
                className={`custom-filter-refinement-list ${attribute} ${!isDrawer ? 'non-drawer-filter' : ''}`}
                data-testid="custom-filter-refinement-list"
            >
                {searchable && (
                    <div className="custom-filter-refinement-list__search-box">
                        <TextField
                            ariaDescribedBy="Search"
                            autoComplete="false"
                            label={placeholder}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                searchForItems(e.currentTarget.value);
                                setSearchFieldValue(e.currentTarget.value);
                            }}
                            value={searchFieldValue}
                        />
                    </div>
                )}

                <div data-testid={`custom-filter-refinement-list-${attribute}`} className={listClasses}>
                    <ul className="custom-filter-refinement-list__container">
                        {items.map(item => (
                            <FilterRefinementListItem
                                key={item.value}
                                attribute={attribute}
                                item={item}
                                refine={refine}
                                handlerAnalytics={handlerAnalytics}
                            />
                        ))}
                    </ul>

                    {!items.length && <div className="custom-filter-refinement-list__no-results">No Results</div>}
                </div>

                {showMore && (
                    <button
                        className="sparky-c-button sparky-c-button--link sparky-u-margin-left-md"
                        onClick={toggleShowMore}
                        disabled={!canToggleShowMore}
                        type="button"
                    >
                        {isShowingMore ? 'Show less' : 'Show more'}
                    </button>
                )}
            </div>

            {showActions && (
                <div className="custom-filter-refinement-list__actions">
                    <Button
                        data-testid={`custom-filter-refinement-list-${attribute}-button-reset-values`}
                        fullWidth
                        onClick={handleClear}
                        text={FILTERS.CLEAR.TITLE}
                        type="button"
                        variant="link"
                        className="custom-filter-refinement-list__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                        disabled={items.length === 0}
                    />
                    <Button
                        data-testid={`custom-filter-refinement-list-${attribute}-button-close`}
                        fullWidth
                        onClick={closeButtonDropdown}
                        text={FILTERS.VIEW_RESULTS.TITLE}
                        type="button"
                        variant="primary"
                        className="custom-filter-refinement-list__actions--view"
                    />
                </div>
            )}
        </>
    );
}
